import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isFirebaseConfigured, firebaseErrorCode } from "@/firebase";
import {
  startAlertLiveListener,
  type AlertLiveConnectionState,
  type AlertLiveEvent,
  type AlertLiveHandle,
} from "@/alerts/integrations/alert-live";
import {
  requestLiveChannelCredentials,
  isLiveChannelAuthError,
} from "@/alerts/api/requests/live-channel.requests";
import { isAlertListQueryKey } from "@/alerts/api/queries/machine-alerts.queries";
import { QUERY_KEYS } from "@/alerts/api/config";
import { alertLiveScopeKey, useAlertLiveStore } from "@/alerts/stores/use-alert-live-store";

/** Coalesce bursts of hints into one refetch. */
const HINT_DEBOUNCE_MS = 500;
/** Backstop poll, so a dead channel still surfaces alerts. Jittered. */
const FALLBACK_MIN_MS = 30_000;
const FALLBACK_MAX_MS = 60_000;
/** Honour a subscribe rate-limit before retrying. */
const SUBSCRIBE_RETRY_MS = 60_000;
/**
 * Let a scope settle before subscribing to it.
 *
 * Boot resolves a default location, then frequently moves to another one a beat
 * later (the org's locations land, the operator's last location is restored, or
 * they switch). Subscribing to the first one costs a credentials request, a
 * Firebase sign-in and a listener attach — all torn down within a second, for a
 * location nobody saw. Worse, two sign-ins in flight at once is precisely the
 * race that once left a listener bound to the wrong scope and answering
 * `permission-denied` (fixed in 0d07f74); not starting the doomed one is better
 * than winning the race with it.
 *
 * The cost is a few hundred milliseconds before hints start flowing, which the
 * REST polls already cover.
 */
const SCOPE_SETTLE_MS = 400;

const randomDelay = (min: number, max: number) => Math.round(min + Math.random() * (max - min));

export interface AlertsLiveScope {
  organizationId: number | null;
  locationId: number | null;
}

/**
 * The application's ONE Firestore live channel for alerts.
 *
 * Mounted in the dashboard layout, not on a page. It used to live on `/map`,
 * which meant the hint stream — and therefore every low-latency alert arrival —
 * existed only while the operator happened to be looking at the map. One
 * subscription for the app is also the correct read of MIN-223's lifecycle
 * rules: a listener per surface would mean several Firebase sessions racing the
 * same scope transitions.
 *
 * On a hint it re-reads canonical state: the alert LISTS (`/map` markers and
 * feed) and the QUEUE (the header bell's source). It deliberately touches
 * nothing else — no history, analytics, activities or actions — so a hint stays
 * a cheap, bounded reconciliation.
 *
 * Firestore is a best-effort HINT channel, never canonical state. When it is
 * unavailable (Firebase unconfigured, project mismatch, subscribe 429/503,
 * listener error) everything still works: the queries keep their own polling and
 * the jittered fallback below forces a re-read regardless. Channel state is
 * published to the shared live store, which the header bell renders.
 *
 * Sound is NOT triggered here. A hint means "something changed", not "the
 * operator must be alarmed" — whether it may sound is decided in
 * `use-alert-notify-feed.ts` against a fresh queue page (MIN-223 § Sound policy).
 */
export const useAlertsLive = ({ organizationId, locationId }: AlertsLiveScope): void => {
  const queryClient = useQueryClient();
  // Each scope change supersedes the previous transition, so a slow subscribe
  // for an abandoned location can never revive itself over the current one.
  const generationRef = useRef(0);

  useEffect(() => {
    if (organizationId == null || locationId == null) return;

    const generation = ++generationRef.current;
    const isStale = () => generationRef.current !== generation;
    const scopeKey = alertLiveScopeKey(organizationId, locationId, []);
    const store = useAlertLiveStore.getState();
    store.beginScope(scopeKey);

    let cancelled = false;
    let handle: AlertLiveHandle | null = null;
    let hintTimer: ReturnType<typeof setTimeout> | undefined;
    // Plain `number`: the ambient Node types make both `setTimeout` and
    // `window.setTimeout` resolve to `Timeout` in this file, which the browser's
    // handle is not.
    let settleTimer: number | undefined;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const setStatus = (...args: Parameters<typeof store.setStatus>) => {
      if (!cancelled && !isStale()) useAlertLiveStore.getState().setStatus(...args);
    };

    /** Re-read canonical state: the map's alert lists AND the bell's queue. */
    const refetchAlerts = () => {
      if (cancelled || isStale()) return;
      void queryClient.invalidateQueries({
        predicate: (query) =>
          isAlertListQueryKey(query.queryKey) || query.queryKey[0] === QUERY_KEYS.ALERTS_QUEUE,
      });
      useAlertLiveStore.getState().recordActivity(scopeKey, {
        kind: "reconcile",
        label: "Alert list refreshed",
      });
    };

    const scheduleFallback = () => {
      if (pollTimer) clearTimeout(pollTimer);
      pollTimer = setTimeout(() => {
        refetchAlerts();
        if (!cancelled) scheduleFallback();
      }, randomDelay(FALLBACK_MIN_MS, FALLBACK_MAX_MS));
    };

    const onEvent = (event: AlertLiveEvent) => {
      if (cancelled || isStale()) return;
      // The whole hint, not just the id: the sound gate needs `signal` and
      // `review_cycle` to decide eligibility (MIN-223 conditions 1 and 5).
      useAlertLiveStore.getState().recordHint(scopeKey, {
        alertId: event.alert_id,
        signal: event.signal,
        reviewCycle: event.review_cycle,
        machineId: event.machine_id,
      });
      // A hidden tab has its timers clamped (to ~1/minute after a few minutes
      // silent), so debouncing there costs real latency for no benefit — there
      // is no UI to coalesce updates for. Re-read immediately instead.
      if (document.hidden) {
        refetchAlerts();
        return;
      }
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(refetchAlerts, HINT_DEBOUNCE_MS);
    };

    const onConnectionChange = (state: AlertLiveConnectionState) => {
      if (state === "connected") setStatus(scopeKey, "live");
      else if (state === "degraded") setStatus(scopeKey, "degraded");
      else setStatus(scopeKey, "connecting");
    };

    /** Resolves after the settle window, or immediately once cancelled. */
    const settled = () =>
      new Promise<void>((resolve) => {
        settleTimer = window.setTimeout(resolve, SCOPE_SETTLE_MS);
      });

    const start = async () => {
      if (!isFirebaseConfigured()) {
        // Perfectly normal in environments without Firebase — poll only.
        setStatus(scopeKey, "polling", "firebase_not_configured");
        return;
      }
      try {
        setStatus(scopeKey, "connecting");
        // A scope abandoned inside this window never reaches the network.
        await settled();
        if (cancelled || isStale()) return;
        const credentials = await requestLiveChannelCredentials(locationId);
        if (cancelled || isStale()) return;
        handle = await startAlertLiveListener({
          customToken: credentials.firebase_custom_token,
          expectedProjectId: credentials.firebase_project_id,
          collectionPath: credentials.events_collection_path,
          isStale: () => cancelled || isStale(),
          // The baseline snapshot is the existing backlog, not news — the
          // listener suppresses hints during it; this just marks us live.
          onBaselineComplete: () => setStatus(scopeKey, "live"),
          onConnectionState: onConnectionChange,
          onHint: onEvent,
          onError: (error) => {
            // Listener/auth failure: drop to REST-only rather than pretending
            // we still have a channel.
            //
            // The CODE is recorded, not just the fact of failure. Firestore's
            // WebChannel answers 200 and reports errors inside the stream, and
            // handling the error here stops the SDK logging it — so a channel
            // that signs in fine and then gets refused by security rules looked
            // from the outside exactly like a healthy one, while every alert
            // silently arrived on the 20s poll instead. The code is safe to
            // surface (`permission-denied`, `unauthenticated`, ...); the token
            // is never touched.
            const code = firebaseErrorCode(error) ?? "unknown";
            setStatus(scopeKey, "polling", `live_channel_unavailable:${code}`);
            useAlertLiveStore.getState().recordActivity(scopeKey, {
              kind: "error",
              label: "Live channel unavailable",
              detail: code,
            });
            console.warn(`[alerts-live] listener error (${code}) — falling back to polling`);
            void handle?.stop();
            handle = null;
          },
        });
        if (cancelled || isStale()) {
          // The scope moved on while we were connecting — never leave a listener
          // bound to a location the operator has left.
          void handle?.stop();
          handle = null;
        }
      } catch (error) {
        if (cancelled || isStale()) return;
        const code = firebaseErrorCode(error) ?? "subscribe_failed";
        setStatus(scopeKey, "polling", `live_channel_unavailable:${code}`);
        console.warn(`[alerts-live] could not start the channel (${code}) — polling`);
        // A hard auth failure means the claims are dead: retrying immediately
        // would just hammer the endpoint. Everything else gets one later retry.
        if (!isLiveChannelAuthError(error)) {
          retryTimer = setTimeout(() => {
            if (!cancelled && !isStale()) void start();
          }, SUBSCRIBE_RETRY_MS);
        }
      }
    };

    // Returning to a visible tab: re-read at once rather than waiting out a
    // throttled fallback timer.
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      refetchAlerts();
      scheduleFallback();
    };
    document.addEventListener("visibilitychange", onVisibility);

    void start();
    scheduleFallback();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (hintTimer) clearTimeout(hintTimer);
      if (settleTimer) window.clearTimeout(settleTimer);
      if (pollTimer) clearTimeout(pollTimer);
      if (retryTimer) clearTimeout(retryTimer);
      void handle?.stop();
      useAlertLiveStore.getState().endScope(scopeKey);
    };
  }, [organizationId, locationId, queryClient]);
};
